import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { MdMenu, MdClose } from 'react-icons/md';

const NavMenuStyles = styled.div`
  position: fixed;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100%;
  padding: 2rem 0;
  background: ${props => props.scrolled ? 'rgba(10, 14, 39, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(10px)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'};
  transition: all var(--transition-normal);
  
  .nav-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    width: 90%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .logo {
    font-size: 2.4rem;
    font-weight: 700;
    font-family: 'Space Grotesk', sans-serif;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.05em;
    cursor: pointer;
    transition: var(--transition-normal);
    
    &:hover {
      transform: scale(1.05);
    }
  }
  
  ul {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  li {
    position: relative;
  }
  
  a {
    display: inline-block;
    font-family: 'Manrope', sans-serif;
    padding: 1rem 2rem;
    font-size: 1.6rem;
    font-weight: 500;
    color: var(--text-secondary);
    border-radius: var(--radius-sm);
    position: relative;
    overflow: hidden;
    transition: var(--transition-normal);
    letter-spacing: -0.01em;
    
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      background: var(--gradient-primary);
      transition: var(--transition-normal);
      transform: translateX(-50%);
    }
    
    &:hover {
      color: var(--text-primary);
      
      &::before {
        width: 80%;
      }
    }
  }
  
  .active {
    color: var(--text-primary);
    background: var(--glass-bg);
    
    &::before {
      width: 80%;
    }
  }
  
  .mobile-menu-icon {
    position: relative;
    width: 4rem;
    height: 4rem;
    cursor: pointer;
    display: none;
    color: var(--text-primary);
    font-size: 3rem;
    z-index: 102;
    
    * {
      pointer-events: none;
    }
  }
  
  .closeNavIcon {
    display: none;
  }
  
  @media only screen and (max-width: 768px) {
    padding: 1.5rem 0;
    
    .nav-wrapper {
      width: 95%;
    }
    
    .mobile-menu-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .hide-item {
      transform: translateX(100%);
      opacity: 0;
    }

    .navItems {
      position: fixed;
      right: 0;
      top: 0;
      height: 100vh;
      width: 280px;
      background: rgba(21, 27, 61, 0.98);
      backdrop-filter: blur(20px);
      padding: 3rem 2rem;
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
      box-shadow: -10px 0 30px rgba(0, 0, 0, 0.5);
      transition: all var(--transition-normal);
      border-left: 1px solid var(--glass-border);
      
      .closeNavIcon {
        display: block;
        width: 3.5rem;
        height: 3.5rem;
        margin: 0 0 3rem auto;
        cursor: pointer;
        color: var(--text-primary);
        font-size: 3rem;
        
        * {
          pointer-events: none;
        }
      }
      
      li {
        width: 100%;
        margin-bottom: 0.5rem;
        
        a {
          width: 100%;
          padding: 1.5rem 2rem;
          font-size: 1.8rem;
          border-radius: var(--radius-md);
          
          &:hover {
            background: var(--glass-bg);
          }
        }
      }
    }
  }
`;

export default function NavMenu() {
  const [showNav, setShowNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <NavMenuStyles scrolled={scrolled}>
      <div className="nav-wrapper">
        <NavLink to="/" className="logo">
          MR
        </NavLink>
        
        <div
          className="mobile-menu-icon"
          onClick={() => setShowNav(!showNav)}
          role="button"
          onKeyDown={() => setShowNav(!showNav)}
          tabIndex={0}
        >
          <MdMenu />
        </div>

        <ul className={showNav ? 'navItems' : 'navItems hide-item'}>
          <div
            className="closeNavIcon"
            onClick={() => setShowNav(false)}
            role="button"
            onKeyDown={() => setShowNav(false)}
            tabIndex={0}
          >
            <MdClose />
          </div>
          <li>
            <NavLink
              to="/"
              onClick={() => setShowNav(false)}
              className={({ isActive }) => isActive ? 'active' : ''}
            >
              Home
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/about"
              onClick={() => setShowNav(false)}
              className={({ isActive }) => isActive ? 'active' : ''}
            >
              About
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/projects"
              onClick={() => setShowNav(false)}
              className={({ isActive }) => isActive ? 'active' : ''}
            >
              Projects
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/contact"
              onClick={() => setShowNav(false)}
              className={({ isActive }) => isActive ? 'active' : ''}
            >
              Contact
            </NavLink>
          </li>
        </ul>
      </div>
    </NavMenuStyles>
  );
}
